import { useContext } from 'react';
import { Formik, Form, Field } from 'formik';
import { Box, Modal, Typography, Grid, IconButton, Button, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiValidSelect from 'src/components/input/MuiValidSelect';
import MuiValidTextField from 'src/components/input/MuiValidTextField';
import { useAPI } from 'src/services/api';
import ToastContext from 'src/store/toast-context';
import RemoveModal from 'src/components/modal/RemoveModal';
import { States } from 'src/util/states';
import LeasedAssetSchema from 'src/components/schemas/leasedAssetSchema';
import ModalStyle from 'src/components/modal/ModalStyle';
import FormObserver from 'src/components/FormObserver';
import LeasedAssetContext from 'src/store/leased-asset-table-context';
import { useMediaQuery } from 'react-responsive';
import WholeNumberInput from 'src/components/input/WholeNumberInput';

const LeasedAssetModal = () => {
  const api = useAPI();
  const { handleError, success } = useContext(ToastContext);
  const { leasedAssetInfo, leasedAssetModal } = useContext(LeasedAssetContext);
  const { leasedAssetModalController, resetAssetModal } = leasedAssetModal;
  const { setLeasedAssets, updating, leasedAssets, initialValues } = leasedAssetInfo;
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });

  const updateRow = (values, args) => {
    setLeasedAssets((prevState) => {
      return prevState.map((row) => {
        if (row.recno === updating.recno) {
          return { ...row, ...values, ...args };
        }
        return row;
      });
    });
  };

  const addNewAsset = async (values) => {
    const { data } = await api.post('/assets/leased', values);
    setLeasedAssets(
      [
        {
          ...values,
          recno: data.recno,
        },
      ].concat(leasedAssets)
    );
  };

  const updateExistingAsset = async (values) => {
    // eslint-disable-next-line no-unused-vars
    const { assignedTo, importDate, updateDate, recno, ...rest } = updating;
    await api.patch(`/assets/leased/${recno}`, {
      ...rest,
      ...values,
    });
    updateRow(values);
  };

  const removeAsset = async () => {
    await api.delete(`/assets/leased/${updating.recno}`);
    setLeasedAssets(leasedAssets.filter((asset) => asset.recno !== updating.recno));
  };

  const handleRemove = async () => {
    resetAssetModal();
    try {
      await removeAsset();
      success('Successfully removed asset');
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  const handleSubmit = async (values) => {
    resetAssetModal();
    try {
      if (updating) {
        await updateExistingAsset(values);
        success('Successfully updated asset');
      } else {
        await addNewAsset(values);
        success('Successfully added asset');
      }
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  return (
    <>
      <Formik
        onSubmit={(values, { resetForm }) => {
          resetForm();
          handleSubmit(values);
        }}
        validationSchema={LeasedAssetSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ resetForm, dirty }) => {
          return (
            <Modal
              open={leasedAssetModalController.open}
              onClose={() => {
                resetForm();
                resetAssetModal();
              }}
            >
              <Box sx={ModalStyle}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: '1rem',
                  }}
                >
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    {updating ? 'Update' : 'Add'} Leased Asset
                  </Typography>
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={() => {
                      resetForm();
                      resetAssetModal();
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <Form>
                  <FormObserver />
                  <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Lessor Name"
                        name="lessorName"
                        variant="standard"
                        inputProps={{ maxLength: 24 }}
                        InputLabelProps={{ shrink: true }}
                        helpText="a person who leases or lets a property to another; a landlord."
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Street Address (Optional Apt/Unit Number)"
                        name="lessorAddr"
                        variant="standard"
                        inputProps={{ maxLength: 24 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="City"
                        name="lessorCity"
                        variant="standard"
                        inputProps={{ maxLength: 15 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidSelect}
                        label="State"
                        name="lessorState"
                        itemValues={States}
                        itemLabels={States}
                        native
                        variant="standard"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Zip"
                        name="lessorZip"
                        variant="standard"
                        inputProps={{ maxLength: 5 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Lease Agreement #"
                        name="agreementNo"
                        variant="standard"
                        inputProps={{ maxLength: 24 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Description"
                        name="descr"
                        variant="standard"
                        inputProps={{ maxLength: 200 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={WholeNumberInput}
                        label="Quantity"
                        name="quantity"
                        variant="standard"
                        inputProps={{ maxLength: 3 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Date of Lease"
                        name="dateOf"
                        variant="standard"
                        placeholder="MM/DD/YYYY"
                        sx={{ width: '100%' }}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Term of Lease"
                        name="term"
                        variant="standard"
                        inputProps={{ maxLength: 8 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={WholeNumberInput}
                        label="Cost at Beginning of Lease - Whole Numbers Only"
                        name="costOf"
                        variant="standard"
                        inputProps={{ maxLength: 7 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={WholeNumberInput}
                        label="Annual Rent - Whole Numbers Only"
                        name="annualRent"
                        variant="standard"
                        inputProps={{ maxLength: 7 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      ></Field>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: '1rem',
                    }}
                  >
                    {updating && (
                      <RemoveModal itemTitle={initialValues.descr} onRemove={handleRemove} />
                    )}
                    <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '0.5rem' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          resetForm();
                          resetAssetModal();
                        }}
                        sx={{ color: 'gray' }}
                      >
                        Close
                      </Button>
                      <Button type="submit" variant="outlined" color="primary" disabled={!dirty}>
                        {isDesktop ? 'Confirm and Save' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              </Box>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

export default LeasedAssetModal;
